import { MapFromFn } from '@mercadona/common/public';
import { Builder } from '@mercadona/common/utils';
import { MTagColor } from '@mercadona/components-musi/tag';

import {
  EvaluationDetailDto,
  EvaluationDto,
  EvaluationListItemChipDto,
  EvaluationListItemDto,
  EvaluationSectionDto,
  EvaluationSectionCardDto,
  EvaluationRemunerationChangesDto,
  EvaluationRemunerationChangesPeriodDto,
  EvaluationRemunerationChangesPeriodFieldDto
} from '@/dtos/evaluation/evaluation.dto';
import {
  EvaluationListItem,
  EvaluationListItemChip,
  EvaluationDetail,
  EvaluationSection,
  EvaluationSectionCard,
  EvaluationRemunerationChanges,
  EvaluationRemunerationChangesPeriod,
  EvaluationRemunerationChangesPeriodField
} from '@/models/evaluation/evaluation.model';

export const mapEvaluationsData: MapFromFn<EvaluationDto, EvaluationListItem[]> = (value) =>
  value.evaluations.map(mapEvaluationListItem);

export const mapEvaluationListItem: MapFromFn<EvaluationListItemDto, EvaluationListItem> = (value) =>
  Builder.forModel(EvaluationListItem)
    .id(value.id)
    .title(value.title)
    .coordinatorName(value.coordinator_name)
    .deliveredAt(value.delivered_at)
    .chips(value.chips.map(mapEvaluationListItemChip))
    .year(value.year)
    .build();

export const mapEvaluationListItemChip: MapFromFn<EvaluationListItemChipDto, EvaluationListItemChip> = (value) =>
  Builder.forModel(EvaluationListItemChip)
    .label(value.label)
    .type(value.type as MTagColor)
    .build();

export const mapEvaluationDetail: MapFromFn<EvaluationDetailDto, EvaluationDetail> = (value) =>
  Builder.forModel(EvaluationDetail)
    .id(value.id)
    .title(value.title)
    .coordinatorName(value.coordinator_name)
    .deliveredAt(value.delivered_at)
    .chips(value.chips.map(mapEvaluationListItemChip))
    .sections(value.sections.map(mapEvaluationSection))
    .remunerationChanges(
      value.remuneration_changes ? mapEvaluationRemunerationChanges(value.remuneration_changes) : null
    )
    .build();

export const mapEvaluationSection: MapFromFn<EvaluationSectionDto, EvaluationSection> = (value) =>
  Builder.forModel(EvaluationSection)
    .title(value.title)
    .chip(value.chip ? mapEvaluationListItemChip(value.chip) : null)
    .html(value.html)
    .cards(value.cards ? value.cards.map(mapEvaluationSectionCard) : null)
    .build();

export const mapEvaluationSectionCard: MapFromFn<EvaluationSectionCardDto, EvaluationSectionCard> = (value) =>
  Builder.forModel(EvaluationSectionCard)
    .chip(value.chip ? mapEvaluationListItemChip(value.chip) : null)
    .title(value.title)
    .imageUrl(value.image_url)
    .tooltip(value.tooltip)
    .build();

export const mapEvaluationRemunerationChanges: MapFromFn<
  EvaluationRemunerationChangesDto,
  EvaluationRemunerationChanges
> = (value) =>
  Builder.forModel(EvaluationRemunerationChanges)
    .headline(value.headline)
    .headlineColor(value.headline_color)
    .periods(value.periods.map(mapEvaluationRemunerationChangesPeriod))
    .build();

export const mapEvaluationRemunerationChangesPeriod: MapFromFn<
  EvaluationRemunerationChangesPeriodDto,
  EvaluationRemunerationChangesPeriod
> = (value) =>
  Builder.forModel(EvaluationRemunerationChangesPeriod)
    .year(value.year)
    .yearType(value.year_type as MTagColor)
    .fields(value.fields.map(mapEvaluationRemunerationChangesPeriodField))
    .build();

export const mapEvaluationRemunerationChangesPeriodField: MapFromFn<
  EvaluationRemunerationChangesPeriodFieldDto,
  EvaluationRemunerationChangesPeriodField
> = (field) =>
  Builder.forModel(EvaluationRemunerationChangesPeriodField).label(field.label).values(field.values).build();
